import React, { useEffect, useRef, useState } from 'react';

export type CameraViewChoice = {
  id: string;
  kind: 'preset' | 'oht' | 'follow';
  label: string;
  hint?: string;
  disabled?: boolean;
};

export type CameraViewGroup = {
  label: string;
  items: CameraViewChoice[];
};

interface CameraViewMenuProps {
  value: string;
  label: string;
  groups: CameraViewGroup[];
  onSelect: (id: string) => void;
}

const CameraViewMenu: React.FC<CameraViewMenuProps> = ({ value, label, groups, onSelect }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="max-w-[280px] truncate rounded-md border border-gray-300 bg-white/95 px-2.5 py-1.5 text-sm font-semibold text-gray-800 shadow hover:bg-white dark:border-gray-600 dark:bg-gray-900/95 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        title="Switch 3D camera view"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 max-h-[min(70vh,480px)] w-80 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {groups.map((group) => (
            <div key={group.label}>
              <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                {group.label}
              </div>
              {group.items.map((item) => {
                const active = item.id === value;
                const disabled = Boolean(item.disabled);
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onSelect(item.id);
                      setOpen(false);
                    }}
                    className={`flex w-full flex-col items-start px-3 py-1.5 text-left ${
                      disabled
                        ? 'cursor-not-allowed text-gray-400'
                        : active
                          ? 'bg-primary-50 text-primary-800 dark:bg-primary-950 dark:text-primary-200'
                          : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className={`text-sm ${active ? 'font-semibold' : ''}`}>{item.label}</span>
                    {item.hint && (
                      <span className="mt-0.5 text-[11px] font-normal leading-snug text-gray-500 dark:text-gray-400">
                        {item.hint}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CameraViewMenu;
